import {
  IInvItemUnits,
  IInvOpsItem,
  IInvOpsKpis,
  IInvOpsRequest,
  IInvOpsStockLocation,
  IInvOpsTransfer,
  IInvReorderRowVM,
  IInvReservation,
} from 'types/inventoryOps.interface'

export const INV_MAIN_WAREHOUSE = 'Main Warehouse'
export const INV_PIT_STORE = 'Pit Store'
export const INV_WORKSHOP_STORE = 'Workshop Store'

const SHORT_LOCATION: { [k: string]: string } = {
  [INV_MAIN_WAREHOUSE]: 'Main',
  [INV_PIT_STORE]: 'Pit',
  [INV_WORKSHOP_STORE]: 'Workshop',
}

export const invShortLocation = (location: string): string => SHORT_LOCATION[location] ?? location

export const formatInvGhs = (value: number): string =>
  'GH₵ ' + new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)

export const formatInvGhsShort = (value: number): string => {
  if (value >= 1000000) return 'GH₵ ' + (value / 1000000).toFixed(2).replace(/\.?0+$/, '') + 'M'
  if (value >= 1000) return 'GH₵ ' + (value / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return formatInvGhs(value)
}

export const INV_LOCATION_VALUES: IInvOpsStockLocation[] = [
  { location: INV_MAIN_WAREHOUSE, valueGhs: 862000 },
  { location: INV_PIT_STORE, valueGhs: 214000 },
  { location: INV_WORKSHOP_STORE, valueGhs: 164000 },
]

export const INV_SITE_STOCK_VALUE_GHS = INV_LOCATION_VALUES.reduce(
  (sum, l) => sum + l.valueGhs,
  0,
)

export const INV_DEMO_ITEMS: IInvOpsItem[] = [
  {
    id: 9001,
    sku: 'FLT-0917',
    name: 'Oil filter — CAT 777',
    itemClass: 'Spares',
    nature: 'CONSUMABLE',
    critical: true,
    stock: [
      { location: INV_MAIN_WAREHOUSE, quantity: 30 },
      { location: INV_WORKSHOP_STORE, quantity: 14 },
    ],
    minLevel: 30,
    reorderPoint: 40,
    leadTimeDays: 14,
    unitCostGhs: 85,
    dailyUsage: 1.5,
    lastMovementDaysAgo: 2,
    movement: 'ACTIVE',
  },
  {
    id: 9002,
    sku: 'TYR-2749',
    name: 'Haul truck tyre 27.00R49',
    itemClass: 'Spares',
    nature: 'CONSUMABLE',
    critical: true,
    stock: [{ location: INV_MAIN_WAREHOUSE, quantity: 6 }],
    minLevel: 8,
    reorderPoint: 10,
    leadTimeDays: 35,
    unitCostGhs: 28400,
    dailyUsage: 0.5,
    lastMovementDaysAgo: 5,
    movement: 'ACTIVE',
  },
  {
    id: 9003,
    sku: 'OIL-HYD208',
    name: 'Hydraulic oil 208 L drum',
    itemClass: 'Consumables',
    nature: 'CONSUMABLE',
    critical: false,
    stock: [
      { location: INV_MAIN_WAREHOUSE, quantity: 12 },
      { location: INV_WORKSHOP_STORE, quantity: 6 },
    ],
    minLevel: 10,
    reorderPoint: 14,
    leadTimeDays: 10,
    unitCostGhs: 3150,
    dailyUsage: 0.6,
    lastMovementDaysAgo: 1,
    movement: 'ACTIVE',
  },
  {
    id: 9004,
    sku: 'GET-3201',
    name: 'Bucket teeth — CAT 320',
    itemClass: 'Spares',
    nature: 'CONSUMABLE',
    critical: true,
    stock: [
      { location: INV_MAIN_WAREHOUSE, quantity: 60 },
      { location: INV_PIT_STORE, quantity: 30 },
    ],
    minLevel: 60,
    reorderPoint: 80,
    leadTimeDays: 21,
    unitCostGhs: 210,
    dailyUsage: 3,
    lastMovementDaysAgo: 3,
    movement: 'ACTIVE',
  },
  {
    id: 9005,
    sku: 'BLT-0800',
    name: 'Conveyor belt roll 800 mm',
    itemClass: 'Spares',
    nature: 'CONSUMABLE',
    critical: true,
    stock: [{ location: INV_MAIN_WAREHOUSE, quantity: 1 }],
    minLevel: 2,
    reorderPoint: 2,
    leadTimeDays: 30,
    unitCostGhs: 46000,
    dailyUsage: 0.022,
    lastMovementDaysAgo: 40,
    movement: 'ACTIVE',
  },
  {
    id: 9006,
    sku: 'DRL-0890',
    name: 'Drill rod 89 mm',
    itemClass: 'Spares',
    nature: 'CONSUMABLE',
    critical: false,
    stock: [{ location: INV_PIT_STORE, quantity: 24 }],
    minLevel: 15,
    reorderPoint: 20,
    leadTimeDays: 18,
    unitCostGhs: 1850,
    dailyUsage: 0.8,
    lastMovementDaysAgo: 4,
    movement: 'ACTIVE',
  },
  {
    id: 9007,
    sku: 'FLT-NAV1',
    name: 'Air filter — Nissan Navara',
    itemClass: 'Spares',
    nature: 'CONSUMABLE',
    critical: false,
    stock: [{ location: INV_WORKSHOP_STORE, quantity: 3 }],
    minLevel: 6,
    reorderPoint: 8,
    leadTimeDays: 7,
    unitCostGhs: 95,
    dailyUsage: 0.15,
    lastMovementDaysAgo: 9,
    movement: 'ACTIVE',
  },
  {
    id: 9008,
    sku: 'WLD-0032',
    name: 'Welding electrodes 3.2 mm (box)',
    itemClass: 'Consumables',
    nature: 'CONSUMABLE',
    critical: false,
    stock: [{ location: INV_WORKSHOP_STORE, quantity: 55 }],
    minLevel: 20,
    reorderPoint: 30,
    leadTimeDays: 7,
    unitCostGhs: 120,
    dailyUsage: 1.8,
    lastMovementDaysAgo: 1,
    movement: 'ACTIVE',
  },
  {
    id: 9009,
    sku: 'PPE-GLV1',
    name: 'Safety gloves (pair)',
    itemClass: 'Consumables',
    nature: 'CONSUMABLE',
    critical: false,
    stock: [{ location: INV_MAIN_WAREHOUSE, quantity: 240 }],
    minLevel: 100,
    reorderPoint: 150,
    leadTimeDays: 5,
    unitCostGhs: 18,
    dailyUsage: 8,
    lastMovementDaysAgo: 1,
    movement: 'ACTIVE',
  },
  {
    id: 9010,
    sku: 'BLT-VCR1',
    name: 'V-belt set — crusher',
    itemClass: 'Spares',
    nature: 'CONSUMABLE',
    critical: false,
    stock: [{ location: INV_WORKSHOP_STORE, quantity: 12 }],
    minLevel: 4,
    reorderPoint: 4,
    leadTimeDays: 14,
    unitCostGhs: 340,
    dailyUsage: 0,
    lastMovementDaysAgo: 270,
    movement: 'SLOW',
  },
  {
    id: 9011,
    sku: 'PMP-IMP3',
    name: 'Pump impeller — bronze',
    itemClass: 'Spares',
    nature: 'CONSUMABLE',
    critical: false,
    stock: [{ location: INV_MAIN_WAREHOUSE, quantity: 4 }],
    minLevel: 2,
    reorderPoint: 2,
    leadTimeDays: 30,
    unitCostGhs: 2900,
    dailyUsage: 0,
    lastMovementDaysAgo: 420,
    movement: 'SLOW',
  },
  {
    id: 9012,
    sku: 'FLT-OLD9',
    name: 'Cabin filter — old model',
    itemClass: 'Spares',
    nature: 'CONSUMABLE',
    critical: false,
    stock: [{ location: INV_MAIN_WAREHOUSE, quantity: 17 }],
    minLevel: 0,
    reorderPoint: 0,
    leadTimeDays: 21,
    unitCostGhs: 60,
    dailyUsage: 0,
    lastMovementDaysAgo: 660,
    movement: 'OBSOLETE',
  },
  {
    id: 9013,
    sku: 'TLS-0450',
    name: 'Torque wrench 3/4″',
    itemClass: 'Spares',
    nature: 'RETURNABLE',
    critical: false,
    stock: [{ location: INV_MAIN_WAREHOUSE, quantity: 4 }],
    minLevel: 2,
    reorderPoint: 2,
    leadTimeDays: 21,
    unitCostGhs: 1150,
    dailyUsage: 0,
    lastMovementDaysAgo: 12,
    movement: 'ACTIVE',
  },
  {
    id: 9014,
    sku: 'RAD-0077',
    name: 'Two-way radio — pit band',
    itemClass: 'Spares',
    nature: 'RETURNABLE',
    critical: false,
    stock: [{ location: INV_MAIN_WAREHOUSE, quantity: 9 }],
    minLevel: 6,
    reorderPoint: 6,
    leadTimeDays: 14,
    unitCostGhs: 780,
    dailyUsage: 0,
    lastMovementDaysAgo: 6,
    movement: 'ACTIVE',
  },
]

export const invNatureLabel = (nature: IInvOpsItem['nature']): string =>
  nature === 'RETURNABLE' ? 'Returnable' : 'Consumable'

export const invTotalOnHand = (item: IInvOpsItem): number =>
  item.stock.reduce((sum, s) => sum + s.quantity, 0)

export const invStockSplitLabel = (item: IInvOpsItem): string =>
  item.stock.map((s) => `${invShortLocation(s.location)} ${s.quantity}`).join(' · ')

export const invBelowMin = (item: IInvOpsItem): boolean => invTotalOnHand(item) < item.minLevel

export const invDaysToStockout = (item: IInvOpsItem): number | null =>
  item.dailyUsage > 0 ? Math.round(invTotalOnHand(item) / item.dailyUsage) : null

export const invSuggestedQty = (item: IInvOpsItem): number =>
  Math.max(item.reorderPoint - invTotalOnHand(item), 0)

export const invStockValueGhs = (item: IInvOpsItem): number =>
  invTotalOnHand(item) * item.unitCostGhs

export const invLastMovementLabel = (days: number): string => {
  if (days <= 1) return days === 1 ? '1 day ago' : 'today'
  if (days < 14) return `${days} days ago`
  if (days < 60) return `${Math.round(days / 7)} weeks ago`
  return `${Math.round(days / 30)} months ago`
}

export const invReorderRows = (items: IInvOpsItem[]): IInvReorderRowVM[] =>
  items
    .filter((item) => invBelowMin(item) || invTotalOnHand(item) <= item.reorderPoint)
    .filter((item) => item.movement === 'ACTIVE')
    .map((item) => ({
      id: item.id,
      sku: item.sku,
      name: item.name,
      critical: item.critical,
      onHand: invTotalOnHand(item),
      minLevel: item.minLevel,
      daysToStockout: invDaysToStockout(item),
      leadTimeDays: item.leadTimeDays,
      suggestedQty: invSuggestedQty(item),
      belowMin: invBelowMin(item),
    }))
    .sort(
      (a, b) =>
        (a.daysToStockout ?? Number.MAX_SAFE_INTEGER) -
        (b.daysToStockout ?? Number.MAX_SAFE_INTEGER),
    )

export const invCriticalItems = (items: IInvOpsItem[]): IInvOpsItem[] =>
  items
    .filter((item) => item.critical)
    .sort((a, b) => Number(invBelowMin(b)) - Number(invBelowMin(a)))

export const invSlowMovers = (items: IInvOpsItem[]): IInvOpsItem[] =>
  items
    .filter((item) => item.movement !== 'ACTIVE')
    .sort((a, b) => b.lastMovementDaysAgo - a.lastMovementDaysAgo)

export const invLocationItemCount = (items: IInvOpsItem[], location: string): number =>
  items.filter((item) => item.stock.some((s) => s.location === location && s.quantity > 0)).length

export const invItemStatus = (
  item: IInvOpsItem,
): { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' } => {
  if (invBelowMin(item)) return { label: 'BELOW MIN', tone: 'danger' }
  if (item.movement === 'OBSOLETE') return { label: 'OBSOLETE', tone: 'neutral' }
  if (item.movement === 'SLOW') return { label: 'SLOW', tone: 'warning' }
  return { label: 'Ok', tone: 'success' }
}

export const invRequestTone = (
  status: IInvOpsRequest['status'],
): 'warning' | 'info' | 'neutral' => {
  if (status === 'URGENT' || status === 'PENDING') return 'warning'
  if (status === 'APPROVED') return 'info'
  return 'neutral'
}

export const INV_DEMO_REQUESTS: IInvOpsRequest[] = [
  {
    id: 'REQ-1042',
    items: 'Haul truck tyre 27.00R49 ×4',
    department: 'Mining',
    ageLabel: '2h ago',
    status: 'URGENT',
    statusLabel: 'URGENT',
    requester: 'K. Owusu',
    costCenter: 'Haulage',
  },
  {
    id: 'REQ-1043',
    items: 'Hydraulic oil 208 L ×6',
    department: 'Maintenance',
    ageLabel: '5h ago',
    status: 'PENDING',
    statusLabel: 'Pending',
    requester: 'A. Serwaa',
    costCenter: 'Workshop',
  },
  {
    id: 'REQ-1039',
    items: 'Bucket teeth — CAT 320 ×40',
    department: 'Pit crew',
    ageLabel: '1d ago',
    status: 'APPROVED',
    statusLabel: 'Awaiting issue',
    requester: 'D. Mensah',
    costCenter: 'Pit operations',
  },
  {
    id: 'REQ-1036',
    items: 'Conveyor belt roll 800 mm ×1',
    department: 'Plant',
    ageLabel: '2d ago',
    status: 'ROUTED',
    statusLabel: 'Procurement',
    requester: 'E. Boateng',
    costCenter: 'Crushing plant',
  },
]

export const INV_DEMO_TRANSFERS: IInvOpsTransfer[] = [
  {
    id: 'TRF-221',
    from: INV_MAIN_WAREHOUSE,
    to: INV_PIT_STORE,
    items: 'Drill rod 89 mm ×8',
    dispatchedLabel: 'today 06:40',
    statusLabel: 'IN TRANSIT',
  },
  {
    id: 'TRF-222',
    from: INV_MAIN_WAREHOUSE,
    to: INV_WORKSHOP_STORE,
    items: 'Oil filter — CAT 777 ×10',
    dispatchedLabel: 'yesterday 15:10',
    statusLabel: 'IN TRANSIT',
  },
]

export const INV_KPIS: IInvOpsKpis = {
  stockValueGhs: INV_SITE_STOCK_VALUE_GHS,
  skuCount: INV_DEMO_ITEMS.length,
  belowMinCount: INV_DEMO_ITEMS.filter(invBelowMin).length,
  openRequests: INV_DEMO_REQUESTS.length,
  urgentRequests: INV_DEMO_REQUESTS.filter((r) => r.status === 'URGENT').length,
  receiptsWeekGhs: 96400,
  receiptsWeekCount: 3,
  inTransitCount: INV_DEMO_TRANSFERS.length,
}

export const INV_LOCATION_SPLIT_LABEL = INV_LOCATION_VALUES.map(
  (l) => `${invShortLocation(l.location)} ${formatInvGhsShort(l.valueGhs).replace('GH₵ ', '')}`,
).join(' · ')

export const invWorstStockoutLabel = (): string => {
  const worst = invReorderRows(INV_DEMO_ITEMS)[0]
  if (!worst || worst.daysToStockout == null) return 'no stockout risk seeded'
  return `worst: tyres — ${worst.daysToStockout} days`
}

export const INV_REORDER_CAPTION = 'Suggested from min/max and recent usage.'

export const INV_CRITICAL_CAPTION =
  'A stockout on any of these stops haulage, digging or the crusher.'

export const INV_DEMO_RESERVATIONS: IInvReservation[] = [
  {
    id: 'RSV-201',
    itemSku: 'GET-3201',
    qty: 40,
    source: 'Request REQ-1039',
    reservedAt: '1d ago',
    location: INV_MAIN_WAREHOUSE,
  },
  {
    id: 'RSV-202',
    itemSku: 'FLT-0917',
    qty: 4,
    source: 'Work order WO-4471 Truck 05 service',
    reservedAt: 'today 08:10',
    location: INV_MAIN_WAREHOUSE,
  },
  {
    id: 'RSV-203',
    itemSku: 'BLT-0800',
    qty: 1,
    source: 'Work order WO-4502 crusher belt change',
    reservedAt: '2d ago',
    location: INV_MAIN_WAREHOUSE,
  },
]

export const invReservationsForSku = (sku: string): IInvReservation[] =>
  INV_DEMO_RESERVATIONS.filter((r) => r.itemSku === sku)

export const invReservedQty = (sku: string): number =>
  invReservationsForSku(sku).reduce((sum, r) => sum + r.qty, 0)

export const invAvailableQty = (item: IInvOpsItem): number =>
  Math.max(invTotalOnHand(item) - invReservedQty(item.sku), 0)

export const invReservationsLabel = (sku: string): string | null => {
  const list = invReservationsForSku(sku)
  if (!list.length) return null
  return list.map((r) => `${r.source} — ${r.qty}`).join(' · ')
}

export const INV_RESERVED_VALUE_GHS = INV_DEMO_RESERVATIONS.reduce((sum, r) => {
  const item = INV_DEMO_ITEMS.find((i) => i.sku === r.itemSku)
  return sum + (item ? item.unitCostGhs * r.qty : 0)
}, 0)

export const INV_RESERVED_COMMITMENTS_LABEL = `against ${INV_DEMO_RESERVATIONS.length} commitments`

export const INV_STOCK_AVAILABLE_CAPTION =
  'Available = on hand − reserved. Reservations come from approved requests and work orders.'

export const INV_REQUESTS_RESERVE_CAPTION =
  'Approval soft-commits stock — Available on the Stock page drops immediately.'

export const INV_ITEM_UNITS: { [sku: string]: IInvItemUnits } = {
  'OIL-HYD208': { purchaseUnit: 'Drum (208 L)', stockUnit: 'L', issueUnit: 'L', factor: 208 },
  'WLD-0032': { purchaseUnit: 'Box (5 kg)', stockUnit: 'kg', issueUnit: 'kg', factor: 5 },
  'PPE-GLV1': { purchaseUnit: 'Carton (50 pr)', stockUnit: 'pair', issueUnit: 'pair', factor: 50 },
  'TYR-2749': { purchaseUnit: 'each', stockUnit: 'each', issueUnit: 'each', factor: 1 },
}

export const invItemUnits = (sku: string): IInvItemUnits =>
  INV_ITEM_UNITS[sku] ?? { purchaseUnit: 'each', stockUnit: 'each', issueUnit: 'each', factor: 1 }

export const invUnitsHint = (sku: string): string | null => {
  const units = INV_ITEM_UNITS[sku]
  if (!units || units.purchaseUnit === units.issueUnit) return null
  return `buy: ${units.purchaseUnit} · issue: ${units.issueUnit}`
}
